import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { PriceContext } from './context';

const TEAMS_URL = 'https://sx7vpiy3ij.execute-api.us-east-2.amazonaws.com/admin_dev/team-pull';

// Fetch today's available teams from the API
const grabTeams = () =>
    axios
        .post(TEAMS_URL, { option: 'team-grabs' })
        .then((res) => res.data?.data ?? []);

// "ATL" and "ATL_10_28.csv" both become "ATL"
const teamCode = (name) => String(name).split('_')[0].trim().toUpperCase();

// Mens nba (Basketball-Reference codes kept internally for API consistency)
const TEAMS = [
    'ATL', 'BOS', 'BRK', 'CHI', 'CHO', 'CLE', 'DAL', 'DEN', 'DET', 'GSW',
    'HOU', 'IND', 'LAC', 'LAL', 'MEM', 'MIL', 'MIN', 'MIA', 'NOP', 'NYK',
    'OKC', 'ORL', 'PHI', 'PHO', 'POR', 'SAC', 'SAS', 'TOR', 'UTA', 'WAS'
];
// const TEAMS = ["ATL","CHI","CON","DAL","GSV","IND","LAS","LVA","MIN","NYL","PHO","POR","SEA","TOR","WAS"];
// Map internal Basketball-Reference codes to official NBA display codes
const NBA_DISPLAY_CODES = {
    BRK: 'BKN',
    CHO: 'CHA',
    PHO: 'PHX'
};

// Helper to get the display code for the UI
const getDisplayCode = (team) => NBA_DISPLAY_CODES[team] || team;

// Styled-components remain unchanged...
const AppContainer = styled.div`
    min-height: 100vh;
    width: 100%;
    background-color: #f9fafb;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    font-family: 'Inter', sans-serif;
    box-sizing: border-box;
`;

const Title = styled.h1`
    font-size: 2rem;
    font-weight: bold;
    color: #2d3748;
    margin-bottom: 2rem;
    border-radius: 0.5rem;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    padding: 1rem 1.5rem;
    background-color: #ffffff;
    text-align: center;

    @media (min-width: 640px) {
        font-size: 2.5rem;
    }
`;

const TeamsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    max-width: 90%;
    width: 100%;
    padding: 0 1rem;
    box-sizing: border-box;

    @media (min-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 1.5rem;
    }

    @media (min-width: 1024px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 2rem;
    }
`;

const TeamBox = styled.div`
    padding: 1rem;
    border-radius: 0.5rem;
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    font-weight: 600;
    transition: all 0.3s ease-in-out;
    min-height: 80px;
    text-align: center;

    &:hover {
        transform: scale(1.03);
        box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
    }

    ${props => props.$isSelected ? `
        background-color: #2563eb;
        color: #ffffff;
        box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
    ` : `
        background-color: #ffffff;
        color: #2d3748;
    `}

    ${props => props.$isDisabled && `
        background-color: #e5e7eb;
        color: #9ca3af;
        cursor: not-allowed;
        box-shadow: none;

        &:hover {
            transform: none;
            box-shadow: none;
        }
    `}
`;

const SelectedTeamsDisplay = styled.p`
    margin-top: 2rem;
    font-size: 1.1rem;
    color: #4a5568;
    padding: 0.75rem 1.5rem;
    background-color: #ffffff;
    border-radius: 0.5rem;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    text-align: center;
    max-width: 90%;
    width: 100%;
    box-sizing: border-box;
`;

const SelectedTeamsSpan = styled.span`
    font-weight: bold;
    color: #2563eb;
`;

const SubmitButton = styled.button`
    ${props => props.disabled && `
        pointer-events: none;
        opacity: 0.5;
    `}
    justify-self: center;
    margin-top: 1rem;
    padding: 10px 20px;
    font-size: 16px;
    color: #ffffff;
    background-color: #007bff;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transition: background-color 0.3s, box-shadow 0.3s;

    &:hover {
        background-color: #0056b3;
        box-shadow: 0 6px 10px rgba(0, 0, 0, 0.2);
    }
`;

// Main Teams component
const Teams = ({ navigate, teamCount }) => {
    const { setTeams } = useContext(PriceContext);

    const [selectedTeamIndices, setSelectedTeamIndices] = useState([]);
    const [activeTeams, setActiveTeams] = useState(new Set());

    useEffect(() => {
        let isMounted = true;
        grabTeams()
            .then((list) => {
                if (isMounted) setActiveTeams(new Set(list.map(teamCode)));
            })
            .catch((err) => console.error(err));
        return () => { isMounted = false; };
    }, []);

    const isActive = (team) => activeTeams.has(team);

    const handleBoxClick = (index) => {
        if (!isActive(TEAMS[index])) return;
        setSelectedTeamIndices((prev) => {
            if (prev.includes(index)) return prev.filter((i) => i !== index);
            if (prev.length < teamCount) return [...prev, index];
            return prev;
        });
    };

    const disabled = selectedTeamIndices.length !== teamCount;

    const handleSubmit = () => {
        // Keeps sending the original internal codes (e.g., BRK, CHO, PHO) to your backend/context
        setTeams(selectedTeamIndices.map((i) => TEAMS[i]));
        navigate('/payment');
    };

    return (
        <AppContainer>
            <Title>Select Your {teamCount} Teams</Title>

            <TeamsGrid>
                {TEAMS.map((team, index) => (
                    <TeamBox
                        key={team}
                        $isSelected={selectedTeamIndices.includes(index)}
                        $isDisabled={!isActive(team)}
                        onClick={() => handleBoxClick(index)}
                    >
                        {/* Renders BKN, CHA, PHX on UI instead of BRK, CHO, PHO */}
                        {getDisplayCode(team)}
                    </TeamBox>
                ))}
            </TeamsGrid>

            <SelectedTeamsDisplay>
                {selectedTeamIndices.length > 0 ? (
                    <>
                        Selected Teams:{' '}
                        <SelectedTeamsSpan>
                            {/* Renders official NBA codes in summary display */}
                            {selectedTeamIndices.map((i) => getDisplayCode(TEAMS[i])).join(', ')}
                        </SelectedTeamsSpan>
                    </>
                ) : (
                    'No teams selected.'
                )}
            </SelectedTeamsDisplay>

            <SubmitButton disabled={disabled} onClick={handleSubmit}>
                Next
            </SubmitButton>
        </AppContainer>
    );
};

export default Teams;